<template>
  <div class="ebmsi-card">
    <div class="ebmsi-header">
      <vue-feather :type="icon" size="15" class="ebmsi-header__icon" />
      <span class="ebmsi-header__title">{{ title }}</span>
    </div>
    <div class="ebmsi-chips">
      <div
        v-for="(value, key) in items"
        :key="key"
        class="ebmsi-chip"
        :class="getChipClass(key, value)"
      >
        <span class="ebmsi-chip__key">{{ formatKey(key) }}</span>
        <span class="ebmsi-chip__sep">·</span>
        <span class="ebmsi-chip__val">{{ formatValue(value) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EbmSettingsItem",
  props: {
    title: { type: String, required: true },
    icon: { type: String, default: "info" },
    items: { type: Object, required: true },
  },
  methods: {
    formatKey(key) {
      return key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    },
    formatValue(value) {
      if (value === null || value === undefined) return "N/A";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (typeof value === "object") {
        if (value.id && value.names) return value.names;
        return JSON.stringify(value);
      }
      return value;
    },
    getChipClass(key, value) {
      if (typeof value === "boolean") {
        return value ? "ebmsi-chip--green" : "ebmsi-chip--muted";
      }
      const k = key.toLowerCase();
      if (k.includes("date")) return "ebmsi-chip--purple";
      if (k.includes("rate") || k.includes("tax")) return "ebmsi-chip--orange";
      if (k.includes("tin") || k.includes("bhf") || k.includes("mrc") || k.includes("dvc")) return "ebmsi-chip--blue";
      if (k.includes("msg") || k.includes("message")) return "ebmsi-chip--teal";
      return "";
    },
  },
};
</script>

<style scoped>
.ebmsi-card {
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 16px;
  display: grid;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem;
}

.ebmsi-header {
  align-items: center;
  display: flex;
  gap: 0.5rem;
}

.ebmsi-header__icon {
  color: var(--hs-primary);
}

.ebmsi-header__title {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 750;
}

.ebmsi-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ebmsi-chip {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-border) 72%, transparent);
  border-radius: 999px;
  display: inline-flex;
  flex: 0 1 auto;
  font-size: 0.79rem;
  gap: 0.32rem;
  max-width: 100%;
  overflow: hidden;
  padding: 0.32rem 0.85rem;
}

.ebmsi-chip--green {
  background: color-mix(in srgb, #10b981 9%, transparent);
  border-color: color-mix(in srgb, #10b981 28%, transparent);
}
.ebmsi-chip--green .ebmsi-chip__key { color: #059669; }
.ebmsi-chip--green .ebmsi-chip__val { color: #047857; font-weight: 750; }

.ebmsi-chip--muted {
  background: color-mix(in srgb, var(--hs-muted) 7%, transparent);
  border-color: color-mix(in srgb, var(--hs-muted) 18%, transparent);
  opacity: 0.75;
}

.ebmsi-chip--blue {
  background: color-mix(in srgb, var(--hs-primary) 9%, transparent);
  border-color: color-mix(in srgb, var(--hs-primary) 26%, transparent);
}
.ebmsi-chip--blue .ebmsi-chip__key { color: var(--hs-primary); }
.ebmsi-chip--blue .ebmsi-chip__val { color: var(--hs-primary-strong, var(--hs-primary)); font-weight: 750; }

.ebmsi-chip--purple {
  background: color-mix(in srgb, #8b5cf6 9%, transparent);
  border-color: color-mix(in srgb, #8b5cf6 26%, transparent);
}
.ebmsi-chip--purple .ebmsi-chip__key { color: #7c3aed; }
.ebmsi-chip--purple .ebmsi-chip__val { color: #6d28d9; font-weight: 750; }

.ebmsi-chip--orange {
  background: color-mix(in srgb, #f59e0b 9%, transparent);
  border-color: color-mix(in srgb, #f59e0b 26%, transparent);
}
.ebmsi-chip--orange .ebmsi-chip__key { color: #d97706; }
.ebmsi-chip--orange .ebmsi-chip__val { color: #b45309; font-weight: 750; }

.ebmsi-chip--teal {
  background: color-mix(in srgb, #14b8a6 9%, transparent);
  border-color: color-mix(in srgb, #14b8a6 26%, transparent);
}
.ebmsi-chip--teal .ebmsi-chip__key { color: #0d9488; }
.ebmsi-chip--teal .ebmsi-chip__val { color: #0f766e; font-weight: 750; }

.ebmsi-chip__key {
  color: var(--hs-muted);
  font-weight: 600;
  white-space: nowrap;
}

.ebmsi-chip__sep {
  color: var(--hs-muted);
  flex-shrink: 0;
  opacity: 0.4;
}

.ebmsi-chip__val {
  color: var(--hs-text);
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 540px) {
  .ebmsi-chip {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 0;
  }
}
</style>
